// =============================================================================
// IMPORTS
// =============================================================================

// React & Router
import { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

// Redux & State
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from '@/state/store';

// Services
import { analyticsService, persistentPrinterService } from '@/services';

// Hooks
import { useAppInitialization } from '@/hooks';
import { TenantProvider, BrandProvider } from '@/hooks';

// Components
import { AppRoutes, ErrorBoundary } from '@/components/core';
import { FullScreenSpinner } from '@/components/ui/Spinner';
import { Toaster } from '@/components/ui/toaster';

// Context & Providers
import { FuseProvider } from '@/context/FuseContext';

// Constants & Config
import { ANALYTICS_CONFIG } from '@/constants/analytics';
import { IS_MOBILE_APP, IS_WEBSITE } from '@/constants/env';

// =============================================================================
// APP CONTENT COMPONENT
// =============================================================================

/**
 * Main app content component that handles routing and layout.
 * Separated from main App component to use router hooks.
 */
function AppContent() {
  // =============================================================================
  // OPTIMIZED INITIALIZATION
  // =============================================================================
  
  const { isAppReady } = useAppInitialization();
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const keyboardListenersRef = useRef<Array<{ remove: () => void }>>([]);

  // =============================================================================
  // KEYBOARD VISIBILITY TRACKING
  // =============================================================================
  
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Handle async listener setup
      Keyboard.addListener('keyboardWillShow', () => {
        setIsKeyboardVisible(true);
      }).then(listener => {
        keyboardListenersRef.current.push(listener);
      });
      
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyboardVisible(false);
      }).then(listener => {
        keyboardListenersRef.current.push(listener);
      });

      return () => {
        // Clean up all listeners
        keyboardListenersRef.current.forEach(listener => {
          listener.remove();
        });
        keyboardListenersRef.current = [];
      };
    }
    return undefined;
  }, []);

  // =============================================================================
  // BROWSER DETECTION - Add class to body for browser-specific CSS
  // =============================================================================
  
  useEffect(() => {
    // Add .is-browser class to body when running in browser (not mobile app)
    // This allows CSS to conditionally apply browser-specific fixes
    if (IS_WEBSITE) {
      document.body.classList.add('is-browser');
    } else {
      document.body.classList.remove('is-browser');
    }
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('is-browser');
    };
  }, []);

  // =============================================================================
  // PRINTER AUTO-RECONNECTION (ONCE)
  // =============================================================================
  
  useEffect(() => {
    // Only attempt printer reconnection on mobile app
    if (IS_MOBILE_APP) {
      // Check for saved printer connection and attempt reconnection
      const savedPrinter = persistentPrinterService.getSavedPrinterConnection();
      if (savedPrinter) {
        // Trigger auto-reconnection once
        persistentPrinterService.startAutoReconnection();
      }
    }
  }, []);

  // =============================================================================
  // LOADING STATE
  // =============================================================================
  
  // Show loading only if critical auth is not ready
  const shouldShowLoading = !isAppReady;

  if (shouldShowLoading) {
    return <FullScreenSpinner text="Initializing your app..." />;
  }

  // =============================================================================
  // RENDER - Safe Area Container
  // =============================================================================
  
  return (
    <div className={`flex flex-col h-screen ${IS_WEBSITE ? 'h-[100dvh]' : ''} w-screen overflow-hidden`}>
      {/* Top safe area - always visible */}
      {IS_MOBILE_APP && <div className="safe-area-top flex-shrink-0" />}
      
      {/* Main content area - constrained by safe areas */}
      {/* position: relative creates new positioning context */}
      {/* isolate creates new stacking context */}
      {/* overflow-hidden clips content to boundaries */}
      <div className="flex-1 overflow-hidden relative isolate">
        <AppRoutes />
      </div>
      
      {/* Bottom safe area - hidden when keyboard is visible */}
      {IS_MOBILE_APP && !isKeyboardVisible && <div className="safe-area-bottom flex-shrink-0" />}
    </div>
  );
}

// =============================================================================
// MAIN APP COMPONENT
// =============================================================================

/**
 * Main App component that sets up all providers and context.
 * Handles analytics initialization and provides the app structure.
 */
function App() {
  // =============================================================================
  // ANALYTICS INITIALIZATION
  // =============================================================================
  
  useEffect(() => {
    analyticsService.init(ANALYTICS_CONFIG.MEASUREMENT_ID);
  }, []);

  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <ErrorBoundary>
      <Router>
        <FuseProvider>
          <PersistGate loading={null} persistor={persistor}>
            <BrandProvider>
              <TenantProvider>
                <AppContent />
                <Toaster />
              </TenantProvider>
            </BrandProvider>
          </PersistGate>
        </FuseProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
